import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import { toast } from 'react-hot-toast';

import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import AssetHeader from './components/AssetHeader';
import BarcodeDisplay from './components/BarcodeDisplay';
import GeneralInformation from './components/GeneralInformation';
import TechnicalSpecifications from './components/TechnicalSpecifications';
import FinancialDetails from './components/FinancialDetails';
import LocationHistory from './components/LocationHistory';
import MaintenanceRecords from './components/MaintenanceRecords';
import OwnerManagement from './components/OwnerManagement';
import StatusUpdateControls from './components/StatusUpdateControls';

const AssetDetailManagement = () => {
  const navigate = useNavigate();
  const [allAssets, setAllAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchAssetId, setSearchAssetId] = useState('');
  const [searchParams] = useSearchParams();
  const searchContainerRef = useRef(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Tabs configuration with icons
  const tabs = [
    {
      id: 'general',
      label: 'General Information',
      icon: 'FileText'
    },
    {
      id: 'technical',
      label: 'Technical Specs',
      icon: 'Cpu'
    },
    {
      id: 'financial',
      label: 'Financial Details',
      icon: 'DollarSign'
    },
    {
      id: 'location',
      label: 'Location History',
      icon: 'MapPin'
    },
    {
      id: 'maintenance',
      label: 'Maintenance',
      icon: 'Wrench'
    },
    {
      id: 'owner',
      label: 'Owner Management',
      icon: 'UserCheck'
    },
    {
      id: 'status',
      label: 'Status Updates',
      icon: 'Activity'
    }
  ];

  // Click outside to close asset search dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load initial asset list
  useEffect(() => {
    const loadAssets = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/assets');
        const list = response.data.data || [];
        setAllAssets(list);

        const assetId = searchParams.get('id');
        if (!assetId && list.length > 0) {
          const firstAsset = list[0];
          navigate(`/asset-detail-management?id=${firstAsset.id}`, { replace: true });
        }
      } catch (error) {
        console.error('Error loading asset list:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAssets();
  }, []);

  // Fetch full details of the active asset
  useEffect(() => {
    const assetId = searchParams.get('id');
    if (!assetId) return;

    const loadSingleAsset = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/assets/${assetId}`);
        if (response.data && response.data.data) {
          setAsset(response.data.data);
          setSearchAssetId(response.data.data.asset_id || '');
          setHasUnsavedChanges(false);
        }
      } catch (error) {
        console.error('Error loading asset details:', error);
        setAsset(null);
        toast.error('Failed to load asset details');
      } finally {
        setLoading(false);
      }
    };

    loadSingleAsset();
  }, [searchParams]);

  // Asset search handler
  const handleSearchChange = (value) => {
    setSearchAssetId(value);

    if (!value.trim()) {
      setFilteredAssets([]);
      setShowDropdown(false);
      return;
    }

    const filtered = allAssets.filter((item) =>
      item.asset_id?.toLowerCase().includes(value.toLowerCase()) ||
      item.name?.toLowerCase().includes(value.toLowerCase()) ||
      item.serial_number?.toLowerCase().includes(value.toLowerCase())
    );

    setFilteredAssets(filtered);
    setShowDropdown(true);
  };

  const handleSelectAsset = (selectedAsset) => {
    setShowDropdown(false);
    setSearchAssetId(selectedAsset.asset_id || '');
    setIsEditing(false);
    setHasUnsavedChanges(false);
    navigate(`/asset-detail-management?id=${selectedAsset.id}`);
  };

  const handleSearchAsset = () => {
    if (!searchAssetId.trim()) return;

    const selected = allAssets.find(
      (item) =>
        item.asset_id?.toLowerCase() === searchAssetId.trim().toLowerCase() ||
        item.serial_number?.toLowerCase() === searchAssetId.trim().toLowerCase() ||
        item.name?.toLowerCase().includes(searchAssetId.trim().toLowerCase())
    );

    if (!selected) {
      toast.error('Asset not found');
      return;
    }

    handleSelectAsset(selected);
  };

  // Update local asset state
  const handleAssetUpdate = (updatedAsset, markUnsaved = true) => {
    setAsset(updatedAsset);
    setHasUnsavedChanges(markUnsaved);
  };

  // Save changes
  const handleSave = async () => {
    if (!asset?.id) return;

    try {
      setSaving(true);
      const response = await axios.put(`/assets/${asset.id}`, asset);

      if (response.data && response.data.success !== false) {
        const updated = response.data.data || response.data;
        setAsset(updated);
        setHasUnsavedChanges(false);
        setIsEditing(false);
        toast.success(response.data.message || 'Asset details saved successfully');

        // Refresh allAssets in background
        const res = await axios.get('/assets');
        setAllAssets(res.data.data || []);
      } else {
        toast.error(response.data?.message || 'Failed to update asset');
      }
    } catch (error) {
      console.error('Error saving asset:', error);
      toast.error(error.response?.data?.message || 'Failed to update asset');
    } finally {
      setSaving(false);
    }
  };

  // Delete asset
  const handleDelete = async () => {
    if (!asset?.id) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete asset "${asset.name || asset.asset_id}"? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      setLoading(true);
      await axios.delete(`/assets/${asset.id}`);
      toast.success('Asset deleted successfully');
      navigate('/asset-management-dashboard');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to delete asset');
    } finally {
      setLoading(false);
    }
  };

  // Edit / Cancel handlers
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = async () => {
    setIsEditing(false);
    setHasUnsavedChanges(false);
    const assetId = searchParams.get('id') || asset?.id;
    if (assetId) {
      try {
        setLoading(true);
        const response = await axios.get(`/assets/${assetId}`);
        if (response.data?.data) {
          setAsset(response.data.data);
          toast.info('Changes discarded');
        }
      } catch (err) {
        console.error('Error reloading asset on cancel:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  // Print asset
  const handlePrint = () => {
    window.print();
  };

  // Loading state
  if (loading && !asset) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-sm text-muted-foreground font-medium">Loading Asset Details...</p>
        </div>
      </div>
    );
  }

  // Not found fallback
  if (!asset) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main className={`transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-64 lg:ml-72'} pt-16`}>
          <div className="p-6 max-w-2xl mx-auto mt-20">
            <Breadcrumb />
            <div className="bg-card border border-border rounded-2xl p-8 shadow-subtle text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
                <Icon name="Search" size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">Search Asset</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter an Asset ID, Asset Name, or Serial Number to view its full details.
                </p>
              </div>

              <div className="relative text-left" ref={searchContainerRef}>
                <input
                  type="text"
                  value={searchAssetId}
                  placeholder="Search Asset ID, Name, Serial Number..."
                  className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  onFocus={() => {
                    setFilteredAssets(allAssets);
                    setShowDropdown(true);
                  }}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && filteredAssets.length > 0) {
                      handleSelectAsset(filteredAssets[0]);
                    }
                  }}
                />

                {showDropdown && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-elevated z-50 max-h-80 overflow-y-auto divide-y divide-border">
                    {filteredAssets.length > 0 ? (
                      filteredAssets.map((item) => (
                        <div
                          key={item.id}
                          className="px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSelectAsset(item)}
                        >
                          <div className="font-semibold text-foreground text-sm">{item.asset_id}</div>
                          <div className="text-xs text-muted-foreground">{item.name}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            Serial: {item.serial_number || 'N/A'}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No Assets Found
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button
                variant="default"
                onClick={handleSearchAsset}
                className="w-full mt-2"
              >
                Search Asset
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <GeneralInformation
            asset={asset}
            isEditing={isEditing}
            onUpdate={handleAssetUpdate}
          />
        );

      case 'technical':
        return (
          <TechnicalSpecifications
            asset={asset}
            isEditing={isEditing}
            onUpdate={handleAssetUpdate}
          />
        );

      case 'financial':
        return (
          <FinancialDetails
            asset={asset}
            isEditing={isEditing}
            onUpdate={handleAssetUpdate}
          />
        );

      case 'location':
        return (
          <LocationHistory
            asset={asset}
            isEditing={isEditing}
            onUpdate={handleAssetUpdate}
          />
        );

      case 'maintenance':
        return (
          <MaintenanceRecords
            asset={asset}
            isEditing={isEditing}
            onUpdate={handleAssetUpdate}
          />
        );

      case 'owner':
        return (
          <OwnerManagement
            asset={asset}
            isEditing={isEditing}
            onUpdate={handleAssetUpdate}
          />
        );

      case 'status':
        return (
          <StatusUpdateControls
            asset={asset}
            isEditing={isEditing}
            onUpdate={handleAssetUpdate}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`transition-layout ${sidebarCollapsed ? 'ml-16' : 'ml-64 lg:ml-72'} pt-16`}>
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
          <Breadcrumb />

          {/* Quick Search Bar */}
          <div className="bg-card border border-border rounded-xl p-3.5 shadow-subtle" ref={searchContainerRef}>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Icon
                  name="Search"
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                />
                <input
                  type="text"
                  value={searchAssetId}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => {
                    setFilteredAssets(allAssets);
                    setShowDropdown(true);
                  }}
                  placeholder="Quick Switch: Search Asset ID, Name, Serial Number..."
                  className="w-full bg-background border border-border rounded-lg pl-10 pr-10 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                {searchAssetId && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchAssetId('');
                      setFilteredAssets([]);
                      setShowDropdown(false);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  >
                    <Icon name="X" size={14} />
                  </button>
                )}

                {showDropdown && filteredAssets.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-elevated z-50 max-h-72 overflow-y-auto divide-y divide-border">
                    {filteredAssets.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => handleSelectAsset(item)}
                        className={`px-4 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors ${
                          item.id === asset.id ? 'bg-primary/5 font-semibold text-primary' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-semibold text-foreground">{item.asset_id}</span>
                          <span className="text-[11px] text-muted-foreground capitalize">{item.status}</span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{item.name}</div>
                        <div className="text-[10px] text-muted-foreground">
                          Serial: {item.serial_number || 'N/A'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                variant="default"
                size="sm"
                onClick={handleSearchAsset}
              >
                Find Asset
              </Button>
            </div>
          </div>

          {/* Sticky Editing Changes Banner */}
          {isEditing && (
            <div className="p-3.5 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-between gap-3 text-xs animate-in fade-in-50">
              <div className="flex items-center space-x-2 text-primary font-semibold">
                <Icon name="Edit3" size={16} />
                <span>
                  Editing Mode Active: Modify fields in any tab and click "Save Changes" to commit.
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="xs"
                  onClick={handleSave}
                  loading={saving}
                >
                  <Icon name="Save" size={13} className="mr-1" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}

          {/* ASSET HEADER (Actions, Status, Title) */}
          <AssetHeader
            asset={asset}
            onEdit={handleEdit}
            onPrint={handlePrint}
            onDelete={handleDelete}
            isEditing={isEditing}
            onSave={handleSave}
            onCancel={handleCancel}
          />

          {/* MAIN WORKSPACE GRID */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">

            {/* LEFT 3 COLUMNS: TABS & ACTIVE TAB CONTENT */}
            <div className="xl:col-span-3 space-y-6">

              {/* TABS BAR */}
              <div className="bg-card border border-border rounded-xl overflow-hidden shadow-subtle">
                <div className="border-b border-border overflow-x-auto scrollbar-none">
                  <nav className="flex px-4 py-1 gap-1">
                    {tabs.map((tab) => {
                      const isActive = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center space-x-2 py-3 px-3.5 text-xs sm:text-sm font-medium transition-all rounded-lg whitespace-nowrap ${
                            isActive
                              ? 'bg-primary/10 text-primary font-bold shadow-xs'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                          }`}
                        >
                          <Icon name={tab.icon} size={15} />
                          <span>{tab.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </div>

              {/* RENDER CURRENT TAB */}
              <div>{renderTabContent()}</div>

            </div>

            {/* RIGHT 1 COLUMN: SIDEBAR (Barcode & Quick Specs) */}
            <div className="xl:col-span-1 space-y-6">

              {/* BARCODE & QR CODE CARD */}
              <BarcodeDisplay asset={asset} />

              {/* ASSET SUMMARY CARD */}
              <div className="bg-card border border-border rounded-xl p-5 shadow-subtle space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-border">
                  <h3 className="font-bold text-sm text-foreground flex items-center space-x-1.5">
                    <Icon name="Info" size={15} className="text-primary" />
                    <span>Quick Summary</span>
                  </h3>
                  <span className="text-[11px] font-mono text-muted-foreground">{asset?.asset_id}</span>
                </div>

                <div className="space-y-3 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Current Status</span>
                    <span className="capitalize font-semibold text-foreground px-2 py-0.5 rounded bg-muted">
                      {asset?.status || 'Unknown'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Assignee / Owner</span>
                    <span className="font-semibold text-foreground">
                      {asset?.owner?.name || asset?.assigned_to_user?.name || 'Unassigned'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Location</span>
                    <span className="text-foreground text-right truncate max-w-[140px]">
                      {asset?.location || 'N/A'} {asset?.room ? `(${asset.room})` : ''}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Department</span>
                    <span className="text-foreground">
                      {asset?.department || 'N/A'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Category</span>
                    <span className="text-foreground">
                      {asset?.category?.name || asset?.asset_category_id || 'General'}
                    </span>
                  </div>

                  {asset?.warranty_expiration && (
                    <div className="flex justify-between items-center pt-2 border-t border-border">
                      <span className="text-muted-foreground">Warranty Expires</span>
                      <span className="text-foreground font-medium">
                        {new Date(asset.warranty_expiration).toLocaleDateString()}
                      </span>
                    </div>
                  )}

                  {/* Financial Snapshot */}
                  <div className="pt-2 border-t border-border space-y-1.5">
                    <div className="flex justify-between items-center text-muted-foreground">
                      <span>Original Cost:</span>
                      <strong className="text-foreground">
                        {asset?.currency ? asset.currency.toUpperCase() : 'INR'}{' '}
                        {(parseFloat(asset?.purchase_price ?? asset?.price ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>
      </main>
    </div>
  );
};

export default AssetDetailManagement;
